import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend
} from "recharts";
import AccountTabs from "@/components/AccountTabs";
import useAccountScope from "@/hooks/useAccountScope";

const CHART_COLORS = ["hsl(221 100% 50%)", "hsl(0 0% 4%)", "hsl(0 100% 58%)", "hsl(43 100% 50%)", "hsl(210 20% 70%)"];

export default function Analytics() {
  const { accounts, selectedAccountId, accountParam, selectedAccount, setSelectedAccountId } = useAccountScope();
  const [senders, setSenders] = useState([]);
  const [byCat, setByCat] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [summary, setSummary] = useState(null);
  const [bulkDeleteExpired, setBulkDeleteExpired] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const params = accountParam ? { account_id: accountParam } : {};
        const [a, b, c, d, e] = await Promise.all([
          api.get("/analytics/top-senders", { params }),
          api.get("/analytics/by-category", { params }),
          api.get("/analytics/deletion-timeline", { params }),
          api.get("/analytics/summary", { params }),
          api.get("/analytics/bulk-delete-status"),
        ]);
        setSenders(a.data);
        setByCat(b.data);
        setTimeline(c.data);
        setSummary(d.data);
        setBulkDeleteExpired(e.data?.expired || false);
      } catch (error) {
        console.error("Failed to load analytics:", error);
      }
    })();
  }, [accountParam]);

  return (
    <div className="p-6 sm:p-8 lg:p-12">
      <div className="mb-8">
        <div className="section-tag mb-2">/ Analytics</div>
        <h1 className="font-display text-3xl sm:text-4xl">Inbox intelligence</h1>
        <AccountTabs
          accounts={accounts}
          selectedAccountId={selectedAccountId}
          onSelect={setSelectedAccountId}
          className="mt-4"
        />
        <p className="text-xs uppercase tracking-widest font-mono text-muted-foreground mt-2">
          Viewing: {selectedAccount ? (selectedAccount.email || selectedAccount.account_id) : "All Accounts"}
        </p>
      </div>

      {bulkDeleteExpired && (
        <div className="mb-8 border border-yellow-400 bg-yellow-50 p-6 rounded">
          <h2 className="font-semibold text-yellow-900 mb-2">Bulk delete period expired</h2>
          <p className="text-sm text-yellow-800 mb-4">
            Your 7-day bulk delete trial has ended. Upgrade to a monthly subscription to unlock bulk delete, autorules, and subscription management.
          </p>
          <Link
            to="/pricing"
            className="inline-block px-4 py-2 bg-yellow-600 text-white text-sm font-semibold rounded hover:bg-yellow-700"
          >
            View pricing
          </Link>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat label="Loaded emails from account" value={summary?.total_emails} />
        <Stat label="Deleted" value={summary?.deleted_emails} />
        <Stat label="Active subs" value={summary?.active_subscriptions} />
        <Stat label="Min saved" value={summary?.time_saved_minutes} />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mb-4">
        <div className="lg:col-span-2 border border-border bg-background p-6">
          <div className="section-tag mb-4">Top senders</div>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={senders} layout="vertical" margin={{ left: 80 }}>
              <CartesianGrid stroke="hsl(214 32% 91%)" />
              <XAxis type="number" stroke="hsl(0 0% 4%)" fontSize={12} />
              <YAxis type="category" dataKey="sender_name" stroke="hsl(0 0% 4%)" fontSize={12} width={120} />
              <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid hsl(0 0% 4%)" }} />
              <Bar dataKey="count" fill="hsl(221 100% 50%)" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="border border-border bg-background p-6">
          <div className="section-tag mb-4">By category</div>
          <ResponsiveContainer width="100%" height={320}>
            <PieChart>
              <Pie data={byCat} dataKey="count" nameKey="category" outerRadius={100} innerRadius={50}>
                {byCat.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid hsl(0 0% 4%)" }} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="border border-border bg-background p-6">
        <div className="section-tag mb-4">Deletions — last 14 days</div>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={timeline}>
            <CartesianGrid stroke="hsl(214 32% 91%)" />
            <XAxis dataKey="date" stroke="hsl(0 0% 4%)" fontSize={11} />
            <YAxis stroke="hsl(0 0% 4%)" fontSize={11} />
            <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid hsl(0 0% 4%)" }} />
            <Line type="monotone" dataKey="count" stroke="hsl(0 100% 58%)" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <p className="text-xs text-muted-foreground mt-6">
        Note: not all emails in your inbox may be loaded.
      </p>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-border bg-background p-5">
      <div className="section-tag mb-2">{label}</div>
      <div className="font-mono text-3xl font-bold">{value ?? "—"}</div>
    </div>
  );
}
